import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database.types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseKey)

export async function isUserAdmin() {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      return false
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/check-admin-status`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({}),
      }
    )

    if (!response.ok) {
      console.error('Error checking admin status:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      })
      return false
    }

    const result = await response.json()
    return result.isAdmin || false
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

export async function createUserWithProfile(
  email: string,
  password: string,
  metadata?: { name?: string }
) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-user-with-profile`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email,
          password,
          metadata,
        }),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create user')
    }

    const result = await response.json()
    return { user: result.user }
  } catch (error: any) {
    if (error.message === 'User already registered') {
      throw new Error('Este email já está registrado. Por favor, faça login.')
    }
    throw error
  }
}

export async function createUserWithProfileViaEdgeFunction(options: {
  email: string
  password?: string
  name: string
  phone?: string
  role?: string
}) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const requestBody: any = {
      email: options.email,
      user_metadata: {
        name: options.name,
        phone: options.phone,
        role: options.role || 'user', // Default to 'user' if no role is provided
      },
    }

    // Only include password if provided
    if (options.password) {
      requestBody.password = options.password
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-user-with-profile`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      }
    )

    if (!response.ok) {
      console.error('Error creating user with profile via edge function:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      })
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create user')
    }

    const result = await response.json()

    return { userId: result.userId }
  } catch (error: any) {
    if (error.message === 'User already registered') {
      throw new Error('Este email já está registrado. Por favor, faça login.')
    }
    throw error
  }
}

export async function sendVerificationEmail(userId: string) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(
    `${supabaseUrl}/functions/v1/send-verification-email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ name: 'Functions', userId }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to send verification email')
  }

  const result = await response.json()
  return result
}

export async function sendVerificationEmailViaEdgeFunction(userId: string) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(
    `${supabaseUrl}/functions/v1/send-verification-email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ name: 'Functions', userId }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to send verification email')
  }

  const result = await response.json()
  return { verificationLink: result.verificationLink }
}

export async function setAdminRole(userId: string, isAdmin: boolean) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/set-admin-role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ userId, isAdmin }),
  })

  if (!response.ok) {
    console.error('Error setting admin role:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      userId,
      isAdmin,
    })
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to set admin role')
  }

  const result = await response.json()
  return result
}

export async function fetchAuthUsers() {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/get-auth-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      name: 'Functions',
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to fetch users')
  }

  const result = await response.json()
  return result.users
}

export interface OwnerFilters {
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export async function fetchOwners(filters: OwnerFilters = {}): Promise<{
  data: any[]
  count: number
  totalPages: number
}> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/get-owners`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      name: 'Functions',
      ...filters,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to fetch owners')
  }

  const result = await response.json()
  return {
    data: result.data || [],
    count: result.count || 0,
    totalPages: result.totalPages || 0,
  }
}

// Legacy function for backward compatibility
export async function fetchAllOwners() {
  const result = await fetchOwners({ limit: 1000 })
  return result.data
}

import type { Property } from '../types'

export type PropertyFiltersStatus =
  Database['public']['Enums']['property_status']

export interface PropertyFilters {
  search?: string
  status?: PropertyFiltersStatus
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
  showDeleted?: boolean // Admin only - show soft-deleted items
}

export const fetchProperties = async (
  filters: PropertyFilters = {}
): Promise<{
  data: Property[]
  count: number
  totalPages: number
}> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/get-properties`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(filters),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to fetch properties')
    }

    const result = await response.json()
    return {
      data: result.data || [],
      count: result.count || 0,
      totalPages: result.totalPages || 0,
    }
  } catch (error) {
    console.error('Error fetching properties:', error)
    throw error
  }
}

export const fetchAllProperties = async (): Promise<Property[]> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/get-properties`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ limit: 1000 }), // Large limit to get all properties
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to fetch properties')
    }

    const result = await response.json()
    return result.data || []
  } catch (error) {
    console.error('Error fetching properties:', error)
    throw error
  }
}

export const fetchPropertyById = async (
  propertyId: string
): Promise<Property> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/get-property-by-id`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ propertyId }),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to fetch property')
    }

    const result = await response.json()
    return result.property
  } catch (error) {
    console.error('Error fetching property:', error)
    throw error
  }
}

export const createProperty = async (
  propertyData: Omit<Property, 'id' | 'created_at' | 'updated_at' | 'code'>
): Promise<Property> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-property`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(propertyData),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create property')
    }

    const result = await response.json()
    return result.property
  } catch (error) {
    console.error('Error creating property:', error)
    throw error
  }
}

export const updateProperty = async (
  propertyData: Partial<Property> & { id: string }
): Promise<Property> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/update-property`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(propertyData),
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to update property')
    }

    const result = await response.json()
    return result.property
  } catch (error) {
    console.error('Error updating property:', error)
    throw error
  }
}

export const fetchUserProfile = async (userId?: string): Promise<any> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No authentication session found')
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/get-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(userId ? { userId } : {}),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to fetch profile')
    }

    const result = await response.json()
    return result.profile
  } catch (error) {
    console.error('Error fetching profile:', error)
    throw error
  }
}

export const updateOwnerViaEdgeFunction = async (
  id: string,
  ownerData: { name?: string; phone?: string }
) => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/update-owner`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ id, ownerData }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to update owner')
  }

  const result = await response.json()
  return result
}
